// ─────────────────────────────────────────────────────────────────────────────
// LootboxScene — animated card reveal for SET 1 "Prismara Awakening"
// ─────────────────────────────────────────────────────────────────────────────
// Receives via scene.start data:
//   { drops, stars, regionId, levelId, nextLevelId, isBoss }
//
// drops = [{ card, rarity, slot }] from lootbox.js::generateLootbox()
// ─────────────────────────────────────────────────────────────────────────────

import Phaser from "phaser";
import { Collection }  from "../game/collection.js";
import { CloudSync }   from "../services/CloudSync.js";
import { ELEMENT_COLOR, RARITY_COLOR } from "../data/cardsData.js";
import { createBackground } from "../vfx/background.js";
import { I18n }        from "../game/I18n.js";

// ── Asset imports ─────────────────────────────────────────────────────────────
import cardBackPrismaraUrl from "../assets/tcg/cards/CARD_BACK_Prismara_Heroes.png";
import cardBackNoxysUrl    from "../assets/tcg/cards/CARD_BACK_Noxys_Corrupted.png";

// Card display dimensions (portrait TCG ratio ~0.71)
const CARD_W = 148;
const CARD_H = 207;

// Detail modal dimensions — fill most of the screen (540x960 viewport)
const DETAIL_W = 460;
const DETAIL_H = 644;

export default class LootboxScene extends Phaser.Scene {
  constructor() { super("LootboxScene"); }

  // ── Preload ──────────────────────────────────────────────────────────────────
  preload() {
    if (!this.textures.exists("tcg-back-prismara"))
      this.load.image("tcg-back-prismara", cardBackPrismaraUrl);
    if (!this.textures.exists("tcg-back-noxys"))
      this.load.image("tcg-back-noxys", cardBackNoxysUrl);

    // Preload card art for each drop
    for (const drop of (this.drops || [])) {
      const card = drop.card;
      if (card.image) {
        const key = `tcg-art-${card.cardId}`;
        if (!this.textures.exists(key)) this.load.image(key, card.image);
      }
    }
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  init(data) {
    this.drops       = data.drops       || [];
    this.stars       = data.stars       || 1;
    this.regionId    = data.regionId    || "volcano";
    this.nextLevelId = data.nextLevelId || null;
    this.isBoss      = data.isBoss      || false;
  }

  // ── Create ───────────────────────────────────────────────────────────────────
  create() {
    const W = this.scale.width;   // 540
    const H = this.scale.height;  // 960

    // Background — inherit region theme
    createBackground(this, this.regionId);

    // Dark overlay so cards stand out
    this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.62).setDepth(0);

    // ── Header ──────────────────────────────────────────────────────────────
    const headerY = 80;
    this.add.text(W / 2, headerY - 24, this.isBoss ? "★ BOSS REWARD ★" : I18n.t('lootbox_header'), {
      fontFamily: "Kamikaze, system-ui",
      fontSize:   this.isBoss ? "28px" : "24px",
      color:      this.isBoss ? "#fbbf24" : "#e2e8f0",
      stroke:     "#000000", strokeThickness: 4,
    }).setOrigin(0.5).setDepth(10);

    this.add.text(W / 2, headerY + 12, I18n.t('tap_each_card'), {
      fontFamily: "system-ui",
      fontSize:   "13px",
      color:      "#94a3b8",
    }).setOrigin(0.5).setDepth(10);

    // ── 3 Cards ──────────────────────────────────────────────────────────────
    const spacing = 20;
    const totalW  = 3 * CARD_W + 2 * spacing;
    const startX  = (W - totalW) / 2 + CARD_W / 2;
    const cardsY  = H * 0.44;

    this._revealed  = 0;
    this._cardObjs  = [];
    this._detailPopup = null;

    for (let i = 0; i < 3; i++) {
      const x    = startX + i * (CARD_W + spacing);
      const drop = this.drops[i];
      const obj  = this._createCard(x, cardsY, drop, i);
      this._cardObjs.push(obj);
    }

    // ── Continue button (hidden until all revealed) ───────────────────────
    this._continueBtn = this._makeContinueBtn(W / 2, H - 80);
    this._continueBtn.setAlpha(0);

    // Consume first — if it returns false, this lootbox was already processed
    // (guards against back-button / scene re-entry duplicating cards)
    const consumed = Collection.consumeLootbox();

    // Detect duplicates BEFORE saving (check which cards already owned)
    const ownedIds = new Set(Collection.getUniqueCardIds());
    this.drops.forEach(d => { d.isDuplicate = ownedIds.has(d.card.id); });

    // Only add cards if we actually consumed a lootbox (prevents dupes on re-entry)
    if (consumed) {
      Collection.addCards(this.drops.map(d => d.card.id));
    }
  }

  // ── Pick the right card back based on deck ───────────────────────────────
  _getCardBackKey(card) {
    return card.element === "corruption" ? "tcg-back-noxys" : "tcg-back-prismara";
  }

  // ── Card factory ──────────────────────────────────────────────────────────
  _createCard(x, y, drop, index) {
    const container = this.add.container(x, y).setDepth(5 + index);
    const backKey = this._getCardBackKey(drop.card);

    // Face-down image (card back — Prismara or Noxys)
    const back = this.add.image(0, 0, backKey)
      .setDisplaySize(CARD_W, CARD_H)
      .setOrigin(0.5);
    container.add(back);

    // "?" indicator
    const qmark = this.add.text(0, 0, "?", {
      fontFamily: "Kamikaze, system-ui",
      fontSize:   "52px",
      color:      drop.card.element === "corruption" ? "#9333ea" : "#60a5fa",
      stroke:     "#000000", strokeThickness: 3,
      alpha:      0.9,
    }).setOrigin(0.5);
    container.add(qmark);

    // Hover glow hint
    container.setSize(CARD_W, CARD_H);
    container.setInteractive({ useHandCursor: true });

    // Entrance animation — stagger per card
    container.setAlpha(0).setScale(0.85);
    this.tweens.add({
      targets: container, alpha: 1, scaleX: 1, scaleY: 1,
      duration: 350, delay: 180 * index, ease: "Back.Out",
    });

    // Tap to reveal
    container.once("pointerdown", () => {
      if (container.getData("flipped")) return;
      container.setData("flipped", true);
      this._flipCard(container, back, qmark, drop);
    });

    return container;
  }

  // ── Flip animation ────────────────────────────────────────────────────────
  _flipCard(container, back, qmark, drop) {
    // Phase 1: squish to 0 width
    this.tweens.add({
      targets: container, scaleX: 0, duration: 160, ease: "Quad.In",
      onComplete: () => {
        // Swap to card front
        back.destroy();
        qmark.destroy();
        this._buildCardFront(container, drop);

        // Phase 2: expand from 0
        this.tweens.add({
          targets: container, scaleX: 1, duration: 200, ease: "Back.Out",
          onComplete: () => {
            this._onCardRevealed(container, drop);
          },
        });
      },
    });
  }

  // ── Build card front inside container ────────────────────────────────────
  _buildCardFront(container, drop) {
    const { card } = drop;
    const artKey = `tcg-art-${card.cardId}`;

    if (card.image && this.textures.exists(artKey)) {
      // Show the real rendered card art — full card image
      const art = this.add.image(0, 0, artKey)
        .setDisplaySize(CARD_W, CARD_H)
        .setOrigin(0.5);
      container.add(art);
    } else {
      // Fallback: simple card with name and element icon
      const bg = this.add.rectangle(0, 0, CARD_W, CARD_H, 0x1e293b)
        .setStrokeStyle(2, parseInt((ELEMENT_COLOR[card.element] || "#94a3b8").replace("#", ""), 16));
      container.add(bg);

      const icons = { fire:"🔥", water:"💧", earth:"🌿", electric:"⚡", aether:"🔮", corruption:"☠" };
      container.add(this.add.text(0, -20, icons[card.element] || "✦", { fontSize: "28px" }).setOrigin(0.5));
      container.add(this.add.text(0, 20, card.name, {
        fontFamily: "Kamikaze, system-ui", fontSize: "10px",
        color: "#e2e8f0", stroke: "#000000", strokeThickness: 2,
        align: "center", wordWrap: { width: CARD_W - 16 },
      }).setOrigin(0.5));
    }
  }

  // ── After each reveal ─────────────────────────────────────────────────────
  _onCardRevealed(container, drop) {
    this._revealed++;

    // Rarity flash overlay
    this._rarityFlash(container, drop.rarity);

    // Duplicate → award fragments and show special pop; otherwise show rarity pop
    if (drop.isDuplicate) {
      const fragAmounts = { common: 15, rare: 25, epic: 40, legendary: 60 };
      const amount = fragAmounts[drop.rarity] || 15;
      Collection.addRawFragments(drop.card.element, amount);
      this._showDuplicatePop(container, amount);
    } else {
      this._showRarityPop(container, drop.rarity);
    }

    // Make the revealed card tappable to show detail modal
    container.removeAllListeners("pointerdown");
    container.setInteractive({ useHandCursor: true });
    container.on("pointerdown", () => this._showDetailModal(drop));

    // All 3 revealed → sync collection to cloud + show continue
    if (this._revealed === 3) {
      // Fire-and-forget — never blocks the player
      CloudSync.syncCollection(Collection.load()).then(result => {
        if (result.success) {
          console.log(`[CloudSync] collection synced (${CloudSync.getShortId()})`);
        }
      });

      this.time.delayedCall(600, () => {
        this.tweens.add({ targets: this._continueBtn, alpha: 1, duration: 400 });
        // Show sync indicator if Supabase is configured
        if (CloudSync.isConfigured()) this._showSyncBadge();

        // Hint text
        const W = this.scale.width;
        const hint = this.add.text(W / 2, this.scale.height * 0.62, I18n.t('tap_card_zoom') || "Toca numa carta para ampliar", {
          fontFamily: "system-ui", fontSize: "12px", color: "#64748b",
        }).setOrigin(0.5).setDepth(10).setAlpha(0);
        this.tweens.add({ targets: hint, alpha: 1, duration: 500, delay: 200 });
      });
    }
  }

  // ── Detail modal — tap a revealed card to see it BIG ────────────────────
  _showDetailModal(drop) {
    if (this._detailPopup) { this._detailPopup.destroy(); this._detailPopup = null; return; }

    const W = this.scale.width;
    const H = this.scale.height;
    const { card, rarity } = drop;
    const artKey = `tcg-art-${card.cardId}`;

    const popup = this.add.container(W / 2, H / 2).setDepth(50);
    this._detailPopup = popup;

    // Dark overlay
    const overlay = this.add.rectangle(0, 0, W, H, 0x000000, 0.82).setInteractive();
    overlay.on("pointerdown", () => { popup.destroy(); this._detailPopup = null; });
    popup.add(overlay);

    if (card.image && this.textures.exists(artKey)) {
      // Large card art — centered, fills most of the screen
      const art = this.add.image(0, -20, artKey)
        .setDisplaySize(DETAIL_W, DETAIL_H)
        .setOrigin(0.5);
      popup.add(art);
    } else {
      // Fallback text card
      const panel = this.add.rectangle(0, -20, DETAIL_W, DETAIL_H, 0x0f172a, 0.97)
        .setStrokeStyle(2, parseInt((RARITY_COLOR[rarity] || "#9ca3af").replace("#",""), 16));
      popup.add(panel);
      popup.add(this.add.text(0, -60, card.name, {
        fontFamily: "Kamikaze, system-ui", fontSize: "26px",
        color: "#e2e8f0", stroke: "#000000", strokeThickness: 3,
      }).setOrigin(0.5));
      if (card.skill) {
        popup.add(this.add.text(0, 0, `${card.skill}\n${card.skillText || ""}`, {
          fontFamily: "system-ui", fontSize: "14px", color: "#94a3b8",
          align: "center", wordWrap: { width: DETAIL_W - 30 },
        }).setOrigin(0.5));
      }
    }

    // Animate in
    popup.setScale(0.7).setAlpha(0);
    this.tweens.add({ targets: popup, scaleX: 1, scaleY: 1, alpha: 1, duration: 220, ease: "Back.Out" });
  }

  _rarityFlash(container, rarity) {
    const colors = { common: 0x9ca3af, rare: 0x60a5fa, epic: 0xc084fc, legendary: 0xfbbf24 };
    const col = colors[rarity] || 0xffffff;

    const flash = this.add.rectangle(container.x, container.y, CARD_W + 20, CARD_H + 20, col, 0.55)
      .setDepth(container.depth + 1);
    this.tweens.add({ targets: flash, alpha: 0, scaleX: 1.4, scaleY: 1.4, duration: 420, ease: "Quad.Out",
      onComplete: () => flash.destroy() });

    // For Legendary: golden particles
    if (rarity === "legendary") {
      const emitter = this.add.particles(container.x, container.y, {
        key:       null, // programmatic
        lifespan:  900,
        speed:     { min: 60, max: 150 },
        scale:     { start: 0.6, end: 0 },
        quantity:  18,
        tint:      0xfbbf24,
        blendMode: "ADD",
      });
      // Use a tiny circle texture as particle
      const gfx = this.make.graphics({ x: 0, y: 0, add: false });
      gfx.fillStyle(0xffffff, 1);
      gfx.fillCircle(4, 4, 4);
      gfx.generateTexture("particle-dot", 8, 8);
      gfx.destroy();
      emitter.setTexture("particle-dot");
      this.time.delayedCall(500, () => emitter.destroy());
    }
  }

  _showRarityPop(container, rarity) {
    const labels = { common: "", rare: "✦ RARE!", epic: "★ EPIC!", legendary: "🌟 LEGENDARY!" };
    const label  = labels[rarity];
    if (!label) return;

    const pop = this.add.text(container.x, container.y - CARD_H / 2 - 18, label, {
      fontFamily: "Kamikaze, system-ui",
      fontSize:   rarity === "legendary" ? "20px" : "16px",
      color:      RARITY_COLOR[rarity],
      stroke:     "#000000", strokeThickness: 3,
    }).setOrigin(0.5).setDepth(20).setAlpha(0).setScale(0.6);

    this.tweens.add({
      targets: pop, alpha: 1, scaleX: 1, scaleY: 1, y: "-=20",
      duration: 300, ease: "Back.Out",
    });
    this.tweens.add({
      targets: pop, alpha: 0, duration: 350, delay: 900, ease: "Quad.In",
      onComplete: () => pop.destroy(),
    });
  }

  _showDuplicatePop(container, fragAmount) {
    const pop = this.add.text(container.x, container.y - CARD_H / 2 - 18, `${I18n.t('duplicate')}\n+${fragAmount} ✦`, {
      fontFamily: "Kamikaze, system-ui",
      fontSize:   "14px",
      color:      "#a78bfa",
      stroke:     "#000000", strokeThickness: 3,
      align:      "center",
    }).setOrigin(0.5).setDepth(20).setAlpha(0).setScale(0.6);

    this.tweens.add({
      targets: pop, alpha: 1, scaleX: 1, scaleY: 1, y: "-=20",
      duration: 300, ease: "Back.Out",
    });
    this.tweens.add({
      targets: pop, alpha: 0, duration: 350, delay: 1100, ease: "Quad.In",
      onComplete: () => pop.destroy(),
    });
  }

  // ── Continue button ───────────────────────────────────────────────────────
  _makeContinueBtn(x, y) {
    const container = this.add.container(x, y).setDepth(20);

    const bg = this.add.rectangle(0, 0, 240, 52, 0x1e3a5f, 1)
      .setStrokeStyle(2, 0x60a5fa)
      .setInteractive({ useHandCursor: true });
    container.add(bg);

    const label = this.add.text(0, 0, this.nextLevelId ? I18n.t('next_level') : I18n.t('map_btn'), {
      fontFamily: "Kamikaze, system-ui",
      fontSize:   "20px",
      color:      "#e2e8f0",
    }).setOrigin(0.5);
    container.add(label);

    bg.on("pointerover",  () => bg.setFillStyle(0x2d5080));
    bg.on("pointerout",   () => bg.setFillStyle(0x1e3a5f));
    bg.on("pointerdown",  () => {
      if (this.nextLevelId) {
        this.scene.start("LevelScene", { regionId: this.regionId, levelId: this.nextLevelId });
      } else {
        this.scene.start("MapScene");
      }
    });

    // Also add "MAPA" as a secondary option when there IS a next level
    if (this.nextLevelId) {
      const mapBtn = this.add.text(0, 36, I18n.t('or_back_map'), {
        fontFamily: "system-ui",
        fontSize:   "12px",
        color:      "#64748b",
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      mapBtn.on("pointerdown", () => this.scene.start("MapScene"));
      mapBtn.on("pointerover",  () => mapBtn.setColor("#94a3b8"));
      mapBtn.on("pointerout",   () => mapBtn.setColor("#64748b"));
      container.add(mapBtn);
    }

    return container;
  }

  // ── Cloud sync indicator ──────────────────────────────────────────────────
  _showSyncBadge() {
    const W   = this.scale.width;
    const col = Collection.load();
    const msg = I18n.t('cloud_sync', { n: col.cards.length, id: CloudSync.getShortId() });

    const badge = this.add.text(W / 2, 26, msg, {
      fontFamily:      "system-ui",
      fontSize:        "9px",
      color:           "#6ee7b7",
      backgroundColor: "#052e16",
      padding:         { x: 10, y: 5 },
    }).setOrigin(0.5).setAlpha(0).setDepth(30);

    this.tweens.add({
      targets:  badge,
      alpha:    0.9,
      duration: 400,
      hold:     3000,
      yoyo:     true,
      ease:     "Sine.InOut",
      onComplete: () => badge.destroy(),
    });
  }
}
